import React, { useId, useState } from 'react';
import { Box } from '@applatform/design-system';
import {
  styled,
  v2Colors,
  v2FontSizes,
  v2FontWeights,
  v2Radii,
  v2Sizes,
} from 'theme';

const REVEAL_INSET = '$space$2';
const REVEAL_WIDTH = '$sizes$16';

const Field = styled('div', {
  position: 'relative',
  mb: '$3',
});

const Control = styled('input', {
  appearance: 'none',
  width: '100%',
  height: v2Sizes.field,
  border: `${v2Sizes.fieldBorder} solid ${v2Colors.line}`,
  borderRadius: v2Radii.control,
  // Typed text starts just below the floated label: its top plus its size.
  pt: `calc($space$2 + ${v2FontSizes.label})`,
  pr: '$3',
  pb: '$2',
  pl: '$3',
  font: 'inherit',
  fontSize: v2FontSizes.input,
  color: v2Colors.ink,
  backgroundColor: '$white',
  outline: 0,
  transition: 'border-color 120ms, box-shadow 120ms',
  '&:focus': {
    borderColor: '$violet900',
    boxShadow: `0 0 0 $space$1 ${v2Colors.focusRing}`,
  },
  variants: {
    hasError: {
      true: {
        borderColor: '$red500',
        '&:focus': {
          borderColor: '$red500',
          boxShadow: `0 0 0 $space$1 ${v2Colors.errorRing}`,
        },
      },
    },
    // Keep a long password clear of RevealButton: its inset plus its width.
    hasReveal: {
      true: {
        pr: `calc(${REVEAL_INSET} + ${REVEAL_WIDTH})`,
      },
    },
  },
});

// The label floats on `:focus` and on `:not(:placeholder-shown)`, which is why
// the input below always carries `placeholder=" "` — a single space. With an
// empty placeholder the selector never matches and the label never lifts.
const FloatingLabel = styled('label', {
  position: 'absolute',
  // Aligned with the typed text: the input's border plus its left padding.
  left: `calc(${v2Sizes.fieldBorder} + $space$3)`,
  top: '$4',
  fontSize: v2FontSizes.input,
  color: v2Colors.faint,
  pointerEvents: 'none',
  transition: 'all 130ms ease',
  [`${Control}:focus + &, ${Control}:not(:placeholder-shown) + &`]: {
    top: '$2',
    fontSize: v2FontSizes.label,
    fontWeight: v2FontWeights.heavy,
    letterSpacing: '$wide',
    color: v2Colors.purple600,
  },
});

const RevealButton = styled('button', {
  position: 'absolute',
  right: REVEAL_INSET,
  // Vertically centred in the field.
  top: `calc((${v2Sizes.field} - $sizes$8) / 2)`,
  height: '$8',
  // Fixed so the input's right padding can reserve exactly this much.
  width: REVEAL_WIDTH,
  px: 0,
  border: 0,
  borderRadius: v2Radii.inset,
  backgroundColor: 'transparent',
  color: v2Colors.muted,
  fontSize: v2FontSizes.small,
  fontWeight: v2FontWeights.semibold,
  cursor: 'pointer',
  '&:hover': { backgroundColor: v2Colors.plane },
  '&:focus-visible': { boxShadow: '$focus' },
});

type AuthFieldProps = React.ComponentPropsWithoutRef<'input'> & {
  readonly id: string;
  readonly name: string;
  readonly label: string;
  readonly error?: string;
};

function AuthField({
  id,
  name,
  label,
  type = 'text',
  error,
  ...rest
}: AuthFieldProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const errorId = useId();
  const isPassword = type === 'password';
  const resolvedType = isPassword && isRevealed ? 'text' : type;

  return (
    <Field>
      <Control
        {...rest}
        id={id}
        name={name}
        type={resolvedType}
        // See FloatingLabel: a single space, not an empty string.
        placeholder=" "
        hasError={Boolean(error)}
        hasReveal={isPassword}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
      />
      <FloatingLabel htmlFor={id}>{label}</FloatingLabel>
      {isPassword ? (
        <RevealButton
          type="button"
          onClick={() => setIsRevealed((shown) => !shown)}
          aria-label={isRevealed ? 'Hide password' : 'Show password'}
        >
          {isRevealed ? 'Hide' : 'Show'}
        </RevealButton>
      ) : null}
      {error ? (
        <Box id={errorId} css={{ mt: '$1', fontSize: '$xs', color: '$red700' }}>
          {error}
        </Box>
      ) : null}
    </Field>
  );
}

export default AuthField;
